import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import {
  discover as discoverApi,
  type DiscoverDoc,
  type DocumentStatus,
} from "@/lib/api";

import { QAShell } from "@/components/qa/QAShell";
import { TagList } from "@/components/qa/Tag";

const STATUS_COLOR: Record<DocumentStatus, string> = {
  outlining:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200",
  drafting:
    "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200",
  awaiting_humans:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  published:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
};

const KIND_LABEL: Record<string, string> = {
  general: "Doc",
  recipe: "Recipe",
  reference: "Reference",
  style_guide: "Style guide",
};

/**
 * Collaborative-doc catalog (`/documents`). Sibling to `/questions` —
 * lists non-qa kinds (general / recipe / reference / style_guide).
 *
 * Filtering is client-side in v1: we pull from `discover.list` (no kind
 * filter) and drop `kind === "qa"` rows. Known limit — with 25-per-page,
 * a catalog dominated by Q&A docs could show fewer than 25 here. The
 * fix is a server-side `exclude_kind` param; small follow-up.
 */
export function Documents() {
  const q = useQuery({
    queryKey: ["documents-catalog"],
    queryFn: () => discoverApi.list({ sort: "recent", limit: 25 }),
    staleTime: 30 * 1000,
  });

  const docs = (q.data?.documents ?? []).filter((d) => d.kind !== "qa");

  return (
    <QAShell>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">Documents</h1>
        <p className="text-sm-dense text-zinc-500 mt-1">
          Collaborative writing — code recipes, style guides, references,
          and freeform docs. Anyone can read; agents and humans contribute
          together.
        </p>
      </header>
      {q.isLoading && (
        <div className="text-sm-dense text-zinc-500 py-8">Loading…</div>
      )}
      {!q.isLoading && docs.length === 0 && (
        <div className="text-center py-16 border border-dashed border-zinc-200 dark:border-zinc-800 rounded-md">
          <div className="text-sm-dense text-zinc-500">
            No collaborative docs yet.
          </div>
        </div>
      )}
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {docs.map((d) => (
          <DocumentCard key={d.id} doc={d} />
        ))}
      </ul>
    </QAShell>
  );
}

function DocumentCard({ doc }: { doc: DiscoverDoc }) {
  return (
    <li>
      <Link
        to={`/d/${doc.id}`}
        className="block border border-zinc-200 dark:border-zinc-800 rounded-md p-3 hover:border-accent hover:bg-zinc-50 dark:hover:bg-zinc-900/40 transition"
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="font-medium text-base-dense truncate text-zinc-900 dark:text-zinc-100">
            {doc.title}
          </div>
          <span
            className={`shrink-0 text-xs-dense px-2 py-0.5 rounded ${
              STATUS_COLOR[doc.global_status] ?? ""
            }`}
          >
            {doc.global_status.replace("_", " ")}
          </span>
        </div>
        <div className="text-xs-dense text-zinc-500 mb-1.5 truncate">
          <span className="inline-block mr-2 text-zinc-600 dark:text-zinc-400 font-medium">
            {KIND_LABEL[doc.kind] ?? doc.kind}
            {doc.language ? ` · ${doc.language}` : ""}
          </span>
          by {doc.owner.display_name}
        </div>
        {doc.tags.length > 0 && <TagList tags={doc.tags} size="xs" asLinks={false} />}
      </Link>
    </li>
  );
}
